import { FEATURE_FLAGS_CHANGED_EVENT } from '@/contexts/FeatureFlagsContext';

import { isNonProduction } from './environmentByHostname';
import { sessionStorageUtils } from './sessionStorage';

const LOCAL_FEATURE_FLAGS_KEY = '__optoscale_local_ff__';

/**
 * Gets all local feature flags from sessionStorage
 */
export function getLocalFeatureFlags(): Record<string, boolean> {
    if (!isNonProduction()) {
        return {};
    }

    return sessionStorageUtils.get(LOCAL_FEATURE_FLAGS_KEY, {});
}

/**
 * Sets a local feature flag
 */
export function setLocalFeatureFlag(flagName: string, enabled: boolean): void {
    if (!isNonProduction()) {
        console.warn('Local feature flags are only available in non-production environments');
        return;
    }

    const flags = getLocalFeatureFlags();
    flags[flagName] = enabled;
    sessionStorageUtils.set(LOCAL_FEATURE_FLAGS_KEY, flags);

    // Dispatch custom event for React synchronization
    window.dispatchEvent(
        new CustomEvent(FEATURE_FLAGS_CHANGED_EVENT, {
            detail: { flags },
        })
    );
}

/**
 * Removes a specific local feature flag
 */
export function removeLocalFeatureFlag(flagName: string): void {
    if (!isNonProduction()) {
        console.warn('Local feature flags are only available in non-production environments');
        return;
    }

    const flags = getLocalFeatureFlags();
    delete flags[flagName];
    sessionStorageUtils.set(LOCAL_FEATURE_FLAGS_KEY, flags);

    // Dispatch custom event for React synchronization
    window.dispatchEvent(
        new CustomEvent(FEATURE_FLAGS_CHANGED_EVENT, {
            detail: { flags },
        })
    );
}

/**
 * Clears all local feature flags
 */
export function clearLocalFeatureFlags(): void {
    if (!isNonProduction()) {
        console.warn('Local feature flags are only available in non-production environments');
        return;
    }

    sessionStorageUtils.remove(LOCAL_FEATURE_FLAGS_KEY);

    // Dispatch custom event for React synchronization
    window.dispatchEvent(
        new CustomEvent(FEATURE_FLAGS_CHANGED_EVENT, {
            detail: { flags: {} },
        })
    );
}

/**
 * Lists all active local feature flags
 */
export function listLocalFeatureFlags(): Record<string, boolean> {
    if (!isNonProduction()) {
        console.warn('Local feature flags are only available in non-production environments');
        return {};
    }

    return getLocalFeatureFlags();
}
